import {
  init,
  replayIntegration,
  setUser,
  setTag,
  setContext,
} from '@sentry/react';
import { User } from '../../network/httpClients/PlatformClient/api.schema';
import {
  getDeploymentConfig,
  getResolvedConfigSource,
} from '../../services/config';
import { isLocalhost } from '../../utils/dynamicSettings';
import {
  getInitClientFromSessionStorage,
  getSessionIdFromSessionStorage,
  getTraceIdFromSessionStorage,
} from '../../utils/url';
import { redactSensitiveKeys } from './utils';

const ERROR_DEDUPLICATE_WINDOW_MS = 60_000;
const recentErrors = new Map<string, number>();

/**
 * Initialize Sentry. Only runs when `config.features.sentry` is present.
 */
export const initSentry = () => {
  const sentryConfig = getDeploymentConfig().features?.sentry;

  if (!sentryConfig?.dsn) {
    if (!isLocalhost()) {
      console.warn('Sentry is not initialized. No DSN in deployment config.');
    }

    return;
  }

  console.log('👓 Initializing Sentry...');

  const appOrigin = window.location.origin;

  init({
    dsn: sentryConfig.dsn,
    tunnel: '/_/e',
    sendDefaultPii: true,
    environment: sentryConfig.environment,
    allowUrls: [new RegExp(appOrigin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))],
    denyUrls: [
      /chrome-extension:\/\//,
      `${appOrigin}/node_modules/@sentry`,
      `${appOrigin}/node_modules/@sentry-internal`,
      `${appOrigin}/node_modules/axios/lib/adapters`,
    ],
    integrations: [
      replayIntegration({
        maskAllInputs: false,
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    beforeSend: (event) => {
      if (event.level !== 'error' && event.level !== 'fatal') {
        return null;
      }

      // Safety net: redact sensitive keys from any event data that reached here
      // with headers still attached (e.g. captures outside captureBillingError).
      if (event.request?.headers) {
        event.request.headers = redactSensitiveKeys(
          event.request.headers,
        ) as Record<string, string>;
      }

      if (event.extra) {
        event.extra = redactSensitiveKeys(event.extra) as Record<
          string,
          unknown
        >;
      }

      if (event.contexts) {
        event.contexts = redactSensitiveKeys(
          event.contexts,
        ) as typeof event.contexts;
      }

      // Redact sensitive keys from breadcrumbs as well, since they can contain request/response data with headers.
      if (event.breadcrumbs?.length) {
        event.breadcrumbs = event.breadcrumbs.map((b) => ({
          ...b,
          data: b.data
            ? (redactSensitiveKeys(b.data) as typeof b.data)
            : b.data,
        }));
      }

      const errorKey = `${event.exception?.values?.[0]?.type ?? ''}: ${
        event.exception?.values?.[0]?.value ?? event.message ?? ''
      }`;
      const now = Date.now();
      const lastSeen = recentErrors.get(errorKey);

      if (
        lastSeen !== undefined &&
        now - lastSeen < ERROR_DEDUPLICATE_WINDOW_MS
      ) {
        return null;
      }

      recentErrors.set(errorKey, now);

      return event;
    },
    ignoreErrors: [
      'Request failed with status code 401',
      'Request failed with status code 403',
      'Missing required parameters',
      'User is not authenticated. Cannot fetch user info',
      'Error invoking post: Method not found',
      'can\'t access property "includes", args.site.enabledFeatures is undefined',
      'Non-Error promise rejection captured', // An error thrown from sentry library. Should be ignored. https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/1
      'the name "transover-type-and-translate-popup" has already been used with this registry',
      `Failed to read a named property 'Element' from 'Window': Blocked a frame with origin "${appOrigin}" from accessing a cross-origin frame.`,
      'Connection to Indexed Database server lost error',
      'UnhandledRejection: Non-Error promise rejection captured with value: ',
    ],
  });

  // Deployment context for correlating errors with the resolved config — surfaces
  // config-resolution regressions (e.g. the PLT-3729 localhost fallback) per event.
  setTag('deployment.source', getResolvedConfigSource() ?? 'unknown');
  setTag('deployment.type', getDeploymentConfig().deployment_type);
};

/**
 * Identify the user in Sentry.
 * @param user - The user to identify.
 */
export const identifySentryUser = (user: User) => {
  const traceId = getTraceIdFromSessionStorage();
  const sessionId = getSessionIdFromSessionStorage();
  const initClient = getInitClientFromSessionStorage();
  const authProtocol = getDeploymentConfig().auth_client_type;

  if (user) {
    setUser({
      id: user.id,
      email: user.email,
      username: user.name,
    });

    setTag('user_id', user.id);
    setTag('user_email', user.email);
    setTag('user_name', user.name);
  }

  if (traceId) {
    setTag('trace_id', traceId);
  }

  if (sessionId) {
    setTag('session_id', sessionId);
  }

  if (initClient) {
    setTag('init_client', initClient);
  }

  if (authProtocol) {
    setTag('auth_protocol', authProtocol);
  }

  if (user.organizationId) {
    setContext('organization', {
      organizationId: user.organizationId,
      organizationPermissions: user.organizationPermission,
    });
  }
};
